// «¿Qué me toca a mí, en qué orden?» — la lista de la oficina en bloques (Entrega 3, Pau aprobó el plan el 04-11:
// web/qa/auditoria-apple/plan.md y orden.md §3 reglas 6, 7, 9 y 11). El servidor decide la situación de cada contratado
// (estado-visual.ts `situacion`); aquí, PURO, a qué grupo y bloque va, con qué icono y color (iguales en la lista, la
// cabecera del chat y la oficina), qué frase lleva la fila y el resumen de la cabecera. Nada de React salvo el tipo de
// los iconos. La lista es la bandeja de entrada; la oficina, la pantalla de inicio.
import { Activity, ClipboardCheck, Handshake, KeyRound, MessageCircle, Moon, Pause, Rocket, TriangleAlert, type LucideIcon } from 'lucide-react';
import { t } from '../../../i18n';
import { aparcado as aparcadoCalculado, proximoCambioAparcar, puedeDormir, type Aparcado, type EntradaAparcar } from '../compartido/aparcar';
import type { ContratadoDTO, Situacion } from '../compartido/vocabulario';
import { hace, ultimoMovimiento } from './estado';

/**
 * El grupo de cada agente: de qué se trata (su icono y su sitio dentro del bloque). `subida` = una subida suya espera tu
 * Allow (una decisión = una fila: la subida va en la fila de su agente, flujo.md #1). `terminado` = te ha escrito porque
 * acabó (sin preguntar nada). `aparcado` = duerme (compartido/aparcar.ts). `se_van` = se está yendo.
 */
export const GRUPOS = ['subida', 'autorizacion', 'bloqueado', 'problema_pau', 'te_escribio', 'terminado', 'con_santi', 'en_marcha', 'problema', 'sin_pendiente', 'aparcado', 'se_van'] as const;
export type Grupo = (typeof GRUPOS)[number];

/**
 * Los bloques de la lista, de arriba abajo (orden.md §3 regla 11, flujo.md #6): Te toca · Han terminado · En marcha y,
 * plegados en una línea, Con Santi · Aparcados · Se van. 04-11: fuera «Fijados» (1 uso en 30 días; lo sustituyen los
 * grupos de mesa de la propuesta 2 y Aparcar) y fuera los grupos sueltos de problema y «sin nada pendiente».
 */
export const BLOQUES = ['te_toca', 'han_terminado', 'en_marcha', 'con_santi', 'aparcados', 'se_van'] as const;
export type Bloque = (typeof BLOQUES)[number];

const BLOQUE: Record<Grupo, Bloque> = {
  subida: 'te_toca', autorizacion: 'te_toca', bloqueado: 'te_toca', problema_pau: 'te_toca', te_escribio: 'te_toca',
  terminado: 'han_terminado',
  en_marcha: 'en_marcha', problema: 'en_marcha', sin_pendiente: 'en_marcha',
  con_santi: 'con_santi', aparcado: 'aparcados', se_van: 'se_van',
};
export const bloqueDe = (g: Grupo): Bloque => BLOQUE[g];

/** Los que van plegados en una línea (se abren al pulsarla). */
export const PLEGADOS: readonly Bloque[] = ['con_santi', 'aparcados', 'se_van'];

/** Dentro de «Te toca»: decisiones con botón → no pueden seguir → preguntas y problema tuyo; en cada nivel, por espera. */
const NIVEL_TE_TOCA: Partial<Record<Grupo, number>> = { subida: 0, autorizacion: 0, bloqueado: 1, problema_pau: 2, te_escribio: 2 };
/** Dentro de «En marcha»: lo que se mueve, luego lo que arregla la oficina sola, luego lo quieto. */
const NIVEL_EN_MARCHA: Partial<Record<Grupo, number>> = { en_marcha: 0, problema: 1, sin_pendiente: 2 };

/** Los tres niveles de «te toca» por la situación del agente. */
export const TE_TOCA: readonly Situacion[] = ['autorizacion', 'bloqueado', 'te_escribio'];

/**
 * 29-10 — ¿se está yendo? (su cierre de verdad, no «integrar y seguir»). Una sola fuente para la lista y la escena: va
 * al bloque «Se van», sin avisos, y en la escena a la salida.
 */
export function seVa(c: Pick<ContratadoDTO, 'situacion'> & Partial<Pick<ContratadoDTO, 'modoCierre'>>): boolean {
  return c.situacion === 'saliendo' || (c.situacion === 'cerrando' && c.modoCierre === 'cerrar');
}

/**
 * Lo que la lista sabe de los contratos con Santi de UN agente (pelotas/porAgente.ts lo calcula de la lista de contratos):
 * `conSanti` = tiene contratos abiertos y en TODOS la pelota la tiene Santi; `esperaTuSi` = hay una tarjeta para Santi
 * esperando el clic de Pau.
 */
export interface SantiDelAgente {
  conSanti: boolean;
  esperaTuSi: boolean;
  /**
   * 04-11 (mesas de equipo) — está «con Santi» por su GRUPO: el grupo está atado a un contrato cuya pelota tiene Santi
   * (pelotas/porAgente.ts). La escena lo apaga en su mesa; la lista lo pone bajo la cabecera de ese grupo en «Con Santi».
   */
  grupo?: string;
}
export type SantiPorAgente = ReadonlyMap<string, SantiDelAgente>;

/**
 * Lo que solo sabe la web de un agente, además de su DTO: si tiene una subida esperando tu Allow (y desde cuándo) y la
 * hora con la que se mira (aparcar depende del reloj; por defecto, ahora).
 */
export interface ContextoAgente {
  subida?: { creadoEn: string } | null;
  ahora?: number;
}
/** Lo mismo para toda la lista: las subidas por agente (despliegues/subidas.ts) y la hora. */
export interface ContextoEquipo {
  subidas?: ReadonlyMap<string, { creadoEn: string }>;
  ahora?: number;
}
const contextoDe = (agentId: string, ctx: ContextoEquipo = {}): ContextoAgente => ({ subida: ctx.subidas?.get(agentId) ?? null, ahora: ctx.ahora });

type ParaGrupo = Pick<ContratadoDTO, 'situacion' | 'pide'> &
  Partial<Pick<ContratadoDTO, 'modoCierre' | 'fase' | 'creadoEn' | 'ultimoDePauEn' | 'aparcadoEn' | 'despertadoEn' | 'ultimoMensaje' | 'ultimoInformeEn' | 'teNecesitaDesde'>>;

/**
 * ¿Duerme? (compartido/aparcar.ts, el mismo cálculo que el servidor). Sin el dato del servidor (`ultimoDePauEn`
 * ausente: un servidor de antes del 04-11 durante una subida, o un dato a medias) nunca se aparca: no se esconde nada
 * por no saber.
 */
export function aparcadoDe(c: ParaGrupo, santi?: SantiDelAgente, ctx: ContextoAgente = {}): Aparcado | null {
  const e = entradaAparcar(c);
  return e ? aparcadoCalculado(e, ctx.ahora ?? Date.now(), contextoAparcar(santi, ctx)) : null;
}

/** Cuándo cambia solo con el reloj (se aparca, o pasa a «proponer cerrar»): la lista pone ahí su temporizador. */
export function proximoCambioDe(c: ParaGrupo, santi?: SantiDelAgente, ctx: ContextoAgente = {}): number | null {
  const e = entradaAparcar(c);
  return e ? proximoCambioAparcar(e, ctx.ahora ?? Date.now(), contextoAparcar(santi, ctx)) : null;
}

/**
 * ¿Se le puede ofrecer «Aparcar»? La regla compartida (compartido/aparcar.ts `puedeDormir`) con el mismo contexto que la
 * lista: los menús de la fila, la mesa y el chat y el «Aparcar los N» de un grupo preguntan aquí (antes, 4 copias que
 * solo miraban la situación).
 */
export function puedeAparcarse(c: Pick<ContratadoDTO, 'situacion'>, santi?: SantiDelAgente, ctx: ContextoAgente = {}): boolean {
  return puedeDormir(c, contextoAparcar(santi, ctx));
}

function entradaAparcar(c: ParaGrupo): EntradaAparcar | null {
  if (c.ultimoDePauEn === undefined || !c.creadoEn) return null;
  return {
    situacion: c.situacion, creadoEn: c.creadoEn, ultimoDePauEn: c.ultimoDePauEn, aparcadoEn: c.aparcadoEn ?? null, despertadoEn: c.despertadoEn ?? null,
    ultimoMensaje: c.ultimoMensaje ?? null, ultimoInformeEn: c.ultimoInformeEn ?? null, teNecesitaDesde: c.teNecesitaDesde ?? null,
  };
}

const contextoAparcar = (santi: SantiDelAgente | undefined, ctx: ContextoAgente) => ({ subida: !!ctx.subida, conSanti: santi?.conSanti, esperaTuSi: santi?.esperaTuSi });

/**
 * El grupo de un contratado. LA clasificación: de ella salen su bloque, si te necesita (`necesitaAPau`), su insignia en
 * la oficina y el resumen de la cabecera. Una subida suya manda siempre (una decisión con botón nunca se esconde, ni en
 * «Se van»); después, el que se va; luego el que duerme; luego Santi y lo que le toca a Pau.
 */
export function grupoDelContratado(c: ParaGrupo, santi?: SantiDelAgente, ctx: ContextoAgente = {}): Grupo {
  if (ctx.subida) return 'subida';
  if (seVa(c)) return 'se_van';
  if (aparcadoDe(c, santi, ctx)) return 'aparcado';
  // Una tarjeta para Santi esperando tu clic te toca: como un mensaje que espera respuesta (si no te toca ya algo más).
  if (santi?.esperaTuSi && !leToca(c)) return 'te_escribio';
  if (santi?.conSanti && !leToca(c)) return 'con_santi';
  if (problemaNecesitaPau(c)) return 'problema_pau';
  if (haTerminado(c)) return 'terminado';
  return grupoDe(c.situacion);
}

function grupoDe(s: Situacion): Grupo {
  if (s === 'trabajando' || s === 'cerrando' || s === 'llegando') return 'en_marcha';
  if (s === 'saliendo') return 'se_van';
  return s;
}

/**
 * Identidad visual de cada situación. Las clases van escritas enteras (Tailwind solo genera lo que lee).
 * Contraste sobre el panel (#121214): todos los colores de texto ≥ 4,5:1 (AA); el icono va sobre su color
 * sólido con tinta oscura, así que nunca depende solo del color: cada situación tiene su icono.
 */
export interface Aspecto {
  /** null = sin insignia (7 · sin nada pendiente: apagado, sin icono que confundir con otro gris). */
  icono: LucideIcon | null;
  /** Texto e icono sueltos en su color. */
  texto: string;
  /** Texto de la frase dentro de una fila teñida (un tono más claro). */
  textoFila: string;
  /** Insignia: fondo sólido + tinta oscura. */
  insignia: string;
  /** Fila teñida (solo 1 y 2: lo que de verdad te para). */
  fila: string | null;
  /** Borde izquierdo de fila y tarjeta. */
  borde: string;
  /** Punto de la etiqueta en la escena cuando no va rellena. */
  punto: string;
}

const ASPECTO: Record<Situacion, Aspecto> = {
  // 1 · el más fuerte: el oro de Venuz (el único oro brillante de la lista: «actúa»), fila teñida + borde +
  // insignia sólida. Llave = permiso.
  autorizacion: {
    icono: KeyRound,
    texto: 'text-oficina-acento',
    textoFila: 'text-oficina-te-necesita-claro',
    insignia: 'bg-oficina-acento text-oficina-acento-tinta',
    fila: 'bg-oficina-acento/[0.13] hover:bg-oficina-acento/[0.19]',
    borde: 'border-oficina-acento',
    punto: 'bg-oficina-acento',
  },
  // 2 · fuerte: violeta, fila teñida + borde. Pausa = «parado hasta que le contestes» (se lee bien a 11 px;
  // la mano quedaba en un borrón). No choca con «Interrumpir»: ese botón solo sale si está trabajando.
  bloqueado: {
    icono: Pause,
    texto: 'text-oficina-situacion-bloqueado',
    textoFila: 'text-oficina-situacion-bloqueado-claro',
    insignia: 'bg-oficina-situacion-bloqueado text-oficina-situacion-bloqueado-tinta',
    fila: 'bg-oficina-situacion-bloqueado/[0.09] hover:bg-oficina-situacion-bloqueado/[0.15]',
    borde: 'border-oficina-situacion-bloqueado',
    punto: 'bg-oficina-situacion-bloqueado',
  },
  // 3 · como un chat sin leer: neutro (blanco), sin teñir; negrita + punto solo si no lo has leído. No va en
  // oro para que el oro siga significando «actúa» (permiso y botones).
  te_escribio: {
    icono: MessageCircle,
    texto: 'text-oficina-texto',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-texto text-oficina-fondo',
    fila: null,
    borde: 'border-oficina-texto2',
    punto: 'bg-oficina-texto',
  },
  // 4 · problema técnico que la oficina está arreglando sola: tono tranquilo (arena), nunca rojo.
  problema: {
    icono: TriangleAlert,
    texto: 'text-oficina-situacion-problema',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-situacion-problema text-oficina-situacion-problema-tinta',
    fila: null,
    borde: 'border-oficina-situacion-problema',
    punto: 'bg-oficina-situacion-problema',
  },
  // 5 · trabajando (y empezando): verde de «en línea», un solo icono.
  trabajando: {
    icono: Activity,
    texto: 'text-oficina-trabajando',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-trabajando text-oficina-trabajando-tinta',
    fila: null,
    borde: 'border-oficina-trabajando',
    punto: 'bg-oficina-trabajando',
  },
  // 6 · cerrando: la oficina comprueba su trabajo. Gris neutro con portapapeles (el único gris con insignia).
  cerrando: {
    icono: ClipboardCheck,
    texto: 'text-oficina-texto2',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-texto2 text-oficina-panel',
    fila: null,
    borde: 'border-oficina-texto2',
    punto: 'bg-oficina-texto2',
  },
  llegando: {
    icono: Activity,
    texto: 'text-oficina-trabajando',
    textoFila: 'text-oficina-texto2',
    insignia: 'bg-oficina-trabajando text-oficina-trabajando-tinta',
    fila: null,
    borde: 'border-oficina-trabajando',
    punto: 'bg-oficina-trabajando',
  },
  // 7 · apagado y SIN insignia.
  sin_pendiente: {
    icono: null,
    texto: 'text-oficina-texto2',
    textoFila: 'text-oficina-texto2',
    insignia: '',
    fila: null,
    borde: 'border-transparent',
    punto: 'bg-oficina-situacion-apagado',
  },
  saliendo: {
    icono: null,
    texto: 'text-oficina-texto2',
    textoFila: 'text-oficina-texto2',
    insignia: '',
    fila: null,
    borde: 'border-transparent',
    punto: 'bg-oficina-situacion-apagado',
  },
};

// Con Santi: el cian de sistema de Apple con el apretón de manos (el mismo en la lista, la oficina y su tablero).
const CON_SANTI: Aspecto = {
  icono: Handshake,
  texto: 'text-oficina-santi',
  textoFila: 'text-oficina-texto2',
  insignia: 'bg-oficina-santi text-oficina-santi-tinta',
  fila: null,
  borde: 'border-oficina-santi',
  punto: 'bg-oficina-santi',
};

// Una subida suya espera tu Allow: es decidir (oro, como el permiso), con el cohete de las subidas.
const SUBIDA: Aspecto = { ...ASPECTO.autorizacion, icono: Rocket };

// Aparcado: duerme en su mesa, apagado y sin insignia; la luna solo en la línea plegada «Aparcados».
const APARCADO: Aspecto = { ...ASPECTO.sin_pendiente, icono: Moon, texto: 'text-oficina-texto2' };

// 4 · problema en el que Pau SÍ tiene que actuar (el agente le pide algo): rojo.
const PROBLEMA_PAU: Aspecto = {
  icono: TriangleAlert,
  texto: 'text-oficina-situacion-problema-pau',
  textoFila: 'text-oficina-situacion-problema-pau',
  insignia: 'bg-oficina-situacion-problema-pau text-oficina-situacion-problema-pau-tinta',
  fila: null,
  borde: 'border-oficina-situacion-problema-pau',
  punto: 'bg-oficina-situacion-problema-pau',
};

/**
 * ¿Un problema técnico necesita a Pau? Solo si el agente le pide algo (`pide`); si no, lo está resolviendo la
 * oficina sola y se pinta tranquilo. (El servidor no da otra señal; ver informe S12.)
 */
export function problemaNecesitaPau(c: Pick<ContratadoDTO, 'situacion' | 'pide'>): boolean {
  return c.situacion === 'problema' && c.pide.trim() !== '';
}

/** El aspecto de UN contratado (igual que el de su situación salvo el problema que necesita a Pau). */
export function aspectoDe(c: Pick<ContratadoDTO, 'situacion' | 'pide'>): Aspecto {
  return problemaNecesitaPau(c) ? PROBLEMA_PAU : ASPECTO[c.situacion];
}

export function aspecto(s: Situacion): Aspecto {
  return ASPECTO[s];
}

/** El aspecto de un grupo (la línea plegada de «Con Santi» y «Aparcados», la fila de una subida). */
export function aspectoGrupo(g: Grupo): Aspecto {
  if (g === 'subida') return SUBIDA;
  if (g === 'con_santi') return CON_SANTI;
  if (g === 'aparcado') return APARCADO;
  if (g === 'problema_pau') return PROBLEMA_PAU;
  if (g === 'se_van') return ASPECTO.saliendo;
  if (g === 'terminado') return ASPECTO.te_escribio;
  return ASPECTO[g === 'en_marcha' ? 'trabajando' : g];
}

/**
 * El aspecto de la fila de un agente en su grupo: el de su situación, salvo su subida (el cohete dorado) y el que duerme
 * (apagado: un aparcado no tiñe ni grita aunque lo último que pidiera fuera urgente).
 */
export function aspectoEnGrupo(c: Pick<ContratadoDTO, 'situacion' | 'pide'>, g: Grupo): Aspecto {
  return g === 'subida' ? SUBIDA : g === 'aparcado' ? APARCADO : aspectoDe(c);
}

/** Desde cuándo te espera (1-3) — la urgencia se ordena por tiempo: el que más lleva, primero. */
export function esperaDesde(c: Pick<ContratadoDTO, 'teNecesitaDesde' | 'ultimoMensaje' | 'ultimoInformeEn' | 'creadoEn'>): string {
  return c.teNecesitaDesde ?? c.ultimoMensaje?.en ?? ultimoMovimiento(c);
}

export function teToca(s: Situacion): boolean {
  return TE_TOCA.includes(s);
}

/** Lo que le toca a Pau por la situación del agente (1-3 y el problema en el que tiene que actuar), sin mirar a Santi. */
function leToca(c: Pick<ContratadoDTO, 'situacion' | 'pide'> & Partial<Pick<ContratadoDTO, 'modoCierre'>>): boolean {
  if (seVa(c)) return false; // 29-10: lo que se va no avisa
  return teToca(c.situacion) || problemaNecesitaPau(c);
}

/**
 * ¿Necesita a Pau? LA definición, para todos (lista, cabecera, riel, oficina, Ctrl+K, pestañas, Alt+↓): está en «Te toca».
 * Antes había tres versiones y la que no sabía de Santi decía «Nada te toca ahora» encima de alguien que esperaba (04-11,
 * auditoría de código B1). Desde la Entrega 3: no cuenta el que duerme (aparcado) ni el que ha terminado (va a «Han
 * terminado», con su «Cerrar»); sí cuenta el que tiene una subida esperando tu Allow.
 */
export function necesitaAPau(c: ParaGrupo, santi?: SantiDelAgente, ctx: ContextoAgente = {}): boolean {
  return bloqueDe(grupoDelContratado(c, santi, ctx)) === 'te_toca';
}

/** Un bloque de la lista con su gente, en su orden. */
export interface GrupoConMiembros<C> {
  bloque: Bloque;
  miembros: C[];
}

/**
 * Los bloques con gente, en su orden. Te toca: por nivel (decidir → no pueden seguir → preguntas) y dentro, el que más
 * lleva esperando primero (lo no leído antes en las preguntas, como un chat). Han terminado: por espera. En marcha: lo
 * que se mueve, lo que arregla la oficina, lo quieto; lo más reciente primero. Aparcados: el que más lleva durmiendo
 * primero (el primero al que proponer cerrar). Con Santi y Se van: lo más reciente.
 */
export function agruparPorSituacion<C extends ContratadoDTO>(lista: readonly C[], noLeido: (c: C) => boolean = () => false, santi?: SantiPorAgente, ctx: ContextoEquipo = {}): GrupoConMiembros<C>[] {
  const ahora = ctx.ahora ?? Date.now();
  const fichas = lista.map((c) => {
    const cx = { ...contextoDe(c.agentId, ctx), ahora };
    const grupo = grupoDelContratado(c, santi?.get(c.agentId), cx);
    // La espera de una subida es la de la subida (lo que decide Pau), no la del agente.
    const espera = cx.subida?.creadoEn ?? esperaDesde(c);
    const dormido = grupo === 'aparcado' ? (aparcadoDe(c, santi?.get(c.agentId), cx)?.desde ?? '') : '';
    return { c, grupo, espera, dormido, movimiento: ultimoMovimiento(c), grupoSanti: santi?.get(c.agentId)?.grupo ?? null };
  });
  type Ficha = (typeof fichas)[number];
  const orden: Record<Bloque, (a: Ficha, b: Ficha) => number> = {
    te_toca: (a, b) =>
      (NIVEL_TE_TOCA[a.grupo] ?? 9) - (NIVEL_TE_TOCA[b.grupo] ?? 9) ||
      (NIVEL_TE_TOCA[a.grupo] === 2 ? Number(noLeido(b.c)) - Number(noLeido(a.c)) : 0) ||
      a.espera.localeCompare(b.espera),
    han_terminado: (a, b) => a.espera.localeCompare(b.espera),
    en_marcha: (a, b) => (NIVEL_EN_MARCHA[a.grupo] ?? 9) - (NIVEL_EN_MARCHA[b.grupo] ?? 9) || b.movimiento.localeCompare(a.movimiento),
    aparcados: (a, b) => a.dormido.localeCompare(b.dormido),
    // 04-11 (mesas de equipo, grupos.md §5): por dentro, primero los que esperan por su grupo, juntos (la lista pone la
    // cabecera del contrato encima de cada grupo); después los que tienen contrato propio. En cada parte, lo más reciente.
    con_santi: (a, b) =>
      Number(!a.grupoSanti) - Number(!b.grupoSanti) || (a.grupoSanti ?? '').localeCompare(b.grupoSanti ?? '') || b.movimiento.localeCompare(a.movimiento),
    se_van: (a, b) => b.movimiento.localeCompare(a.movimiento),
  };
  return BLOQUES.map((bloque) => ({
    bloque,
    miembros: fichas.filter((f) => bloqueDe(f.grupo) === bloque).sort(orden[bloque]).map((f) => f.c),
  })).filter((g) => g.miembros.length > 0);
}

/** Orden plano (para Alt+↑/↓ y el riel): el mismo que la lista en bloques. */
export function ordenarPorSituacion<C extends ContratadoDTO>(lista: readonly C[], noLeido?: (c: C) => boolean, santi?: SantiPorAgente, ctx?: ContextoEquipo): C[] {
  return agruparPorSituacion(lista, noLeido, santi, ctx).flatMap((g) => g.miembros);
}

const PASO_CIERRE: Record<NonNullable<ContratadoDTO['pasoCierre']>, string> = {
  esperando_informe: 'oficinaV2.s12.cierre.esperando_informe',
  comprobando: 'oficinaV2.s12.cierre.comprobando',
  revisando: 'oficinaV2.s12.cierre.revisando',
  esperando_permiso: 'oficinaV2.s12.cierre.esperando_permiso',
  publicando: 'oficinaV2.s12.cierre.publicando',
  en_cola: 'oficinaV2.s12.cierre.en_cola',
  archivando: 'oficinaV2.s12.cierre.archivando',
};

/**
 * WS15-D8 — el paso del cierre en una frase. Integrando (sigue después), lo que escribe es su «nota de entrega»; al irse,
 * su «informe de cierre» (Pau, 17-10: que se llamaran igual «me confunde»). Lo usan la fila y el expediente.
 */
export function pasoCierreEnFrase(paso: NonNullable<ContratadoDTO['pasoCierre']>, modo: 'cerrar' | 'integrar' | null | undefined): string {
  if (modo === 'integrar' && paso === 'esperando_informe') return t('oficinaV2.s12.cierre.esperando_nota');
  return t(PASO_CIERRE[paso] as 'oficinaV2.s12.cierre.comprobando');
}

/**
 * 09-10 (Pau aprobó «Terminado» frente a «te pregunta»): en «Esperan tu respuesta», ¿es porque ha acabado su trabajo
 * (te lo entrega) y no porque te pregunte algo? El agente lo dice en su parte (fase «cerrando»: está acabando);
 * «terminado» lo pone la oficina al irse. Solo cuenta si NO te ha dejado una pregunta (`pide`).
 */
export function haTerminado(c: Pick<ContratadoDTO, 'situacion' | 'pide'> & Partial<Pick<ContratadoDTO, 'fase'>>): boolean {
  return c.situacion === 'te_escribio' && (c.fase === 'terminado' || c.fase === 'cerrando') && c.pide.trim() === '';
}

/**
 * 09-10 — «sin noticias · 25 min»: trabaja, pero lleva rato sin contar nada (el servidor decide `sinNoticias`, 15 min).
 * La fila lo dice en vez de la hora; null si tiene noticias.
 */
export function sinNoticiasDesde(c: Pick<ContratadoDTO, 'sinNoticias' | 'ultimoMensaje' | 'ultimoInformeEn' | 'creadoEn'>, ahora = Date.now()): string | null {
  if (!c.sinNoticias) return null;
  const min = Math.max(0, Math.round((ahora - Date.parse(ultimoMovimiento(c))) / 60_000));
  if (!Number.isFinite(min)) return null;
  const h = Math.round(min / 60);
  const cuanto =
    min < 60 ? t('oficinaV2.s12.duracion.min', { n: min }) : h < 24 ? t('oficinaV2.s12.duracion.h', { n: h }) : t('oficinaV2.s12.duracion.d', { n: Math.round(h / 24) });
  return t('oficinaV2.s12.sinNoticias', { cuanto });
}

/**
 * La UNA frase útil de la fila (brief: sin repetir el nombre del grupo). Para 1 la acción que pide («Pide
 * permiso: …»); para 2 lo que necesita en sus palabras; para 3 su último mensaje o su pregunta («Terminado: …» si
 * ha acabado su trabajo).
 */
export function fraseDeFila(c: Pick<ContratadoDTO, 'situacion' | 'pide' | 'titular' | 'ultimoMensaje' | 'pasoCierre'> & Partial<Pick<ContratadoDTO, 'modoCierre' | 'fase'>>): string {
  const pide = c.pide.trim();
  const titular = c.titular.trim();
  switch (c.situacion) {
    case 'autorizacion':
      // Sin «Pide permiso:» (el grupo ya lo dice); en la franja del chat sí va.
      return pide ? mayuscula(pide) : t('oficinaV2.s12.fila.permisoSinTexto');
    case 'bloqueado':
      return pide || t('oficinaV2.s12.fila.bloqueadoSinTexto');
    case 'te_escribio': {
      if (haTerminado(c)) {
        // Sin «Terminado: Terminado: …» si su mensaje ya empieza así.
        const que = (c.ultimoMensaje?.texto.trim() || titular).replace(/^(terminado|finished|done)\b\s*[:.,-]?\s*/i, '');
        return t('oficinaV2.s12.fila.terminado', { que: que ? mayuscula(que) : t('oficinaV2.s12.fila.terminadoSinTexto') });
      }
      return pide || c.ultimoMensaje?.texto.trim() || titular || t('oficinaV2.s12.fila.teEscribioSinTexto');
    }
    case 'problema':
      if (pide) return pide;
      return t('oficinaV2.s12.fila.problemaOficina', { que: titular || t('oficinaV2.s12.fila.problemaSinTexto') });
    case 'trabajando':
      return titular || t('oficinaV2.s12.fila.trabajandoSinTexto');
    case 'cerrando': {
      const paso = c.pasoCierre ? pasoCierreEnFrase(c.pasoCierre, c.modoCierre) : t('oficinaV2.s12.fila.cerrandoSinTexto');
      // 30-09 — integrar sin irse: que no parezca que se va.
      return c.modoCierre === 'integrar' ? t('oficinaV2.s12.fila.integrando', { paso: paso.charAt(0).toLowerCase() + paso.slice(1) }) : paso;
    }
    case 'llegando':
      return t('oficinaV2.s12.fila.llegando');
    case 'saliendo':
      return t('oficinaV2.s12.fila.saliendo');
    default:
      return titular || t('oficinaV2.s12.fila.sinPendienteSinTexto');
  }
}

function mayuscula(x: string): string {
  return x.charAt(0).toUpperCase() + x.slice(1);
}

/** Nombre corto de la situación (cabecera del chat y lectores de pantalla). */
export function etiquetaSituacion(s: Situacion): string {
  return t(`oficinaV2.s12.situacion.${s}`);
}

/**
 * ¿Lleva el punto de «sin leer»? Como un chat: lo decide el servidor (`noLeido`), pero lo que se va y lo que duerme no
 * hacen ruido. Una sola regla para la fila y el riel plegado (antes el riel encendía el punto a los que se iban).
 */
export function puntoSinLeer(c: Pick<ContratadoDTO, 'noLeido' | 'situacion'> & Partial<Pick<ContratadoDTO, 'modoCierre'>>, grupo: Grupo): boolean {
  return c.noLeido && !seVa(c) && grupo !== 'aparcado';
}

/** El título de un bloque de la lista («Te toca», «Han terminado»…). */
export function tituloBloque(b: Bloque): string {
  return t(`oficinaV2.lista.bloque.${b}`);
}

/**
 * La línea de un aparcado: desde cuándo duerme y, a los 7 días, la propuesta de cerrarlo (una pista, no un botón: lo
 * decide Pau abriendo su chat o con «Cerrar» del menú).
 */
export function fraseAparcado(a: Aparcado, ahora = Date.now()): string {
  const dias = Math.floor((ahora - Date.parse(a.desde)) / 86_400_000);
  if (a.proponerCerrar) return t('oficinaV2.lista.aparcado.proponerCerrar', { n: dias });
  return t(a.por === 'pau' ? 'oficinaV2.lista.aparcado.porPau' : 'oficinaV2.lista.aparcado.solo', { cuando: hace(a.desde, ahora) });
}

export interface Pieza {
  grupo: Grupo;
  n: number;
  texto: string;
}

/**
 * El resumen de la oficina (el letrero de cada sala, mundo/letrero.ts). `teToca`: «2 permisos · 2 no pueden seguir · 1
 * problema para ti · 3 te han escrito» (solo lo que hay y con la misma clasificación que la lista: sin los aparcados ni
 * los que han terminado). `aparte`: «4 trabajando · 1 cerrando · 1 con un problema» (este último: lo que arregla la oficina).
 */
type ParaResumen = ParaGrupo & Pick<ContratadoDTO, 'agentId'>;
export function resumenCabecera(lista: readonly ParaResumen[], santi?: SantiPorAgente, ctx: ContextoEquipo = {}): { teToca: Pieza[]; aparte: Pieza[] } {
  const conGrupo = lista.map((c) => ({ c, g: grupoDelContratado(c, santi?.get(c.agentId), contextoDe(c.agentId, ctx)) }));
  const cuenta = (f: (x: (typeof conGrupo)[number]) => boolean) => conGrupo.filter(f).length;
  const de = (g: Grupo) => (x: { g: Grupo }) => x.g === g;
  const pieza = (grupo: Grupo, n: number, uno: string, varios: string): Pieza[] =>
    n ? [{ grupo, n, texto: t((n === 1 ? uno : varios) as 'oficinaV2.s12.resumen.permisoUno', { n }) }] : [];
  return {
    teToca: [
      ...pieza('subida', cuenta(de('subida')), 'oficinaV2.lista.resumen.subidaUna', 'oficinaV2.lista.resumen.subidaVarias'),
      ...pieza('autorizacion', cuenta(de('autorizacion')), 'oficinaV2.s12.resumen.permisoUno', 'oficinaV2.s12.resumen.permisoVarios'),
      ...pieza('bloqueado', cuenta(de('bloqueado')), 'oficinaV2.s12.resumen.bloqueadoUno', 'oficinaV2.s12.resumen.bloqueadoVarios'),
      ...pieza('problema_pau', cuenta(de('problema_pau')), 'oficinaV2.s12.resumen.problemaPauUno', 'oficinaV2.s12.resumen.problemaPauVarios'),
      ...pieza('te_escribio', cuenta(de('te_escribio')), 'oficinaV2.s12.resumen.teEscribioUno', 'oficinaV2.s12.resumen.teEscribioVarios'),
    ],
    aparte: [
      ...pieza('en_marcha', cuenta((x) => x.g === 'en_marcha' && (x.c.situacion === 'trabajando' || x.c.situacion === 'llegando')), 'oficinaV2.s12.resumen.trabajando', 'oficinaV2.s12.resumen.trabajando'),
      // 29-10: los que se van no cuentan (están al fondo, sin ruido); solo los que cierran para integrar y seguir.
      ...pieza('en_marcha', cuenta((x) => x.g === 'en_marcha' && x.c.situacion === 'cerrando'), 'oficinaV2.s12.resumen.cerrando', 'oficinaV2.s12.resumen.cerrando'),
      ...pieza('problema', cuenta(de('problema')), 'oficinaV2.s12.resumen.problemaUno', 'oficinaV2.s12.resumen.problemaVarios'),
    ],
  };
}

/** Marcador de cada agente en la oficina de monigotes (insignia, etiqueta y si le toca a quien mira). */
export interface MarcadorS12 {
  /** Le toca a quien mira (necesitaAPau y es de su bandeja): cuenta para «↓ te necesita» y la cámara de la oficina. */
  destaca: boolean;
  /** Insignia sobre la cabeza + etiqueta rellena (lo de «Te toca»; el que ha terminado, la suya blanca); null = sin insignia. */
  icono: LucideIcon | null;
  relleno: string | null;
  punto: string | null;
  late: boolean;
  texto: string;
  /** 04-11 — duerme (aparcado): la oficina lo pinta dormido, sin insignia ni punto. */
  aparcado?: boolean;
  /** 04-11 — su grupo de mesas espera a Santi (y a él no le toca nada): la oficina lo pinta apagado, sin punto. */
  apagado?: boolean;
}

/**
 * La misma clasificación que la lista (`grupoDelContratado`). Quien monta la escena pasa el contexto que tenga (sus
 * subidas, la hora); sin él, la subida no se marca, pero nunca se contradice a la lista en lo demás.
 */
export function marcadorEscena(c: ContratadoDTO, santi?: SantiDelAgente, ctx: ContextoAgente = {}): MarcadorS12 {
  const g = grupoDelContratado(c, santi, ctx);
  const destaca = bloqueDe(g) === 'te_toca';
  // Una tarjeta para Santi que espera tu clic se marca como «te ha escrito» (su grupo en la lista).
  const a = g === 'te_escribio' && !leToca(c) ? ASPECTO.te_escribio : aspectoEnGrupo(c, g);
  // El que ha terminado lleva su insignia blanca de «te ha escrito», pero no destaca: no te para nada.
  const insignia = destaca || g === 'terminado';
  const dormido = g === 'aparcado';
  // Mesas de equipo (regla 9): está «con Santi» por su grupo → apagado en su mesa. Si le toca algo, ya no está aquí (regla 10).
  const apagado = g === 'con_santi' && !!santi?.grupo;
  return {
    destaca,
    icono: insignia ? a.icono : null,
    relleno: insignia ? a.insignia : null,
    punto: dormido || apagado || c.situacion === 'sin_pendiente' || c.situacion === 'saliendo' ? null : a.punto,
    // Solo late lo que de verdad le para (decidir, no puede seguir, su problema); «te ha escrito» no alarma.
    late: destaca && (g === 'subida' || g === 'autorizacion' || g === 'bloqueado' || g === 'problema_pau'),
    texto: fraseDeFila(c),
    ...(dormido ? { aparcado: true } : {}),
    ...(apagado ? { apagado: true } : {}),
  };
}
